import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useSnackbar } from 'notistack';
import loteInformesService from '../../services/loteInformesService';
import type { LoteMovimientoDto, LoteSaldoDto } from '../../types/lotes-informes';
import { getApiErrorMessage } from '../../utils/apiError';

function etiquetaTercero(m: LoteMovimientoDto): string {
  const tipo = String(m.tipoCliente ?? '').trim().toUpperCase();
  const rol = tipo === 'P' ? 'Prov.' : tipo === 'C' ? 'Cli.' : '';
  const nro = m.nroTercero != null && m.nroTercero > 0 ? String(m.nroTercero) : '';
  const nombre = String(m.nombreTercero ?? '').trim();
  if (!nro && !nombre) return '—';
  const quien = [nro, nombre].filter(Boolean).join(' · ');
  return rol ? `${rol} ${quien}` : quien;
}

const LotesSaldosInforme: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<LoteSaldoDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [codigoArticulo, setCodigoArticulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [nroLote, setNroLote] = useState('');

  const [detalleOpen, setDetalleOpen] = useState(false);
  const [detalleSaldo, setDetalleSaldo] = useState<LoteSaldoDto | null>(null);
  const [detalleMovs, setDetalleMovs] = useState<LoteMovimientoDto[]>([]);
  const [detalleLoading, setDetalleLoading] = useState(false);

  const load = async () => {
    try {
      const data = await loteInformesService.saldos({
        codigoArticulo: codigoArticulo || undefined,
        descripcion: descripcion || undefined,
        nroLote: nroLote || undefined,
        page: page + 1,
        pageSize,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al consultar'), { variant: 'error' });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const abrirDetalleMovimientos = async (saldo: LoteSaldoDto) => {
    setDetalleSaldo(saldo);
    setDetalleOpen(true);
    setDetalleLoading(true);
    setDetalleMovs([]);
    try {
      const data = await loteInformesService.movimientos({
        idLote: saldo.idLote,
        nroUbicacion: saldo.nroUbicacion,
        page: 1,
        pageSize: 200,
      });
      setDetalleMovs(data.items);
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al cargar movimientos del lote'), {
        variant: 'error',
      });
    } finally {
      setDetalleLoading(false);
    }
  };

  const exportExcel = () => {
    const rows = items.map((i) => ({
      Artículo: i.codigoArticulo,
      Descripción: i.descripcionArticulo ?? '',
      Lote: i.nroLote,
      Depósito: `${i.nroUbicacion} ${i.nombreUbicacion ?? ''}`.trim(),
      Vencimiento: i.fechaVencimiento ? String(i.fechaVencimiento).slice(0, 10) : '',
      Cantidad: i.cantidad,
      Estado: i.estado === 'B' ? 'Bloqueado' : 'Activo',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Saldos');
    XLSX.writeFile(wb, 'lotes-saldos.xlsx');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
            Saldos de inventario por lote/serie
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              label="Artículo"
              value={codigoArticulo}
              onChange={(e) => setCodigoArticulo(e.target.value)}
            />
            <TextField
              size="small"
              label="Descripción"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              sx={{ minWidth: { sm: 220 } }}
            />
            <TextField size="small" label="Lote" value={nroLote} onChange={(e) => setNroLote(e.target.value)} />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => {
                setPage(0);
                load();
              }}
            >
              Buscar
            </Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportExcel}>
              Excel
            </Button>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Artículo</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Lote</TableCell>
                  <TableCell>Depósito</TableCell>
                  <TableCell>Vencimiento</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center" sx={{ width: 56 }}>
                    Mov.
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((i) => (
                  <TableRow key={`${i.idLote}-${i.nroUbicacion}`}>
                    <TableCell>{i.codigoArticulo}</TableCell>
                    <TableCell sx={{ maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {i.descripcionArticulo || '—'}
                    </TableCell>
                    <TableCell>{i.nroLote}</TableCell>
                    <TableCell>
                      {i.nroUbicacion} {i.nombreUbicacion}
                    </TableCell>
                    <TableCell>
                      {i.fechaVencimiento ? String(i.fechaVencimiento).slice(0, 10) : '—'}
                      {i.vencido && <Chip size="small" color="error" label="Vencido" sx={{ ml: 1 }} />}
                      {i.proximoAVencer && <Chip size="small" color="warning" label="Próximo" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell align="right">{i.cantidad}</TableCell>
                    <TableCell>{i.estado === 'B' ? 'Bloqueado' : 'Activo'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver movimientos del lote">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => void abrirDetalleMovimientos(i)}
                          aria-label={`Movimientos lote ${i.nroLote}`}
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </CardContent>
      </Card>

      <Dialog
        open={detalleOpen}
        onClose={() => setDetalleOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Movimientos del lote {detalleSaldo?.nroLote ?? ''}
          <Typography variant="body2" color="text.secondary">
            {detalleSaldo
              ? `${detalleSaldo.codigoArticulo} · Dep. ${detalleSaldo.nroUbicacion} ${detalleSaldo.nombreUbicacion ?? ''} · Saldo ${detalleSaldo.cantidad}`
              : ''}
          </Typography>
          <IconButton
            aria-label="Cerrar"
            onClick={() => setDetalleOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detalleLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Tipo documento</TableCell>
                    <TableCell>Nº documento</TableCell>
                    <TableCell>Cliente / Proveedor</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detalleMovs.map((m) => (
                    <TableRow key={m.idMovimiento}>
                      <TableCell>{new Date(m.fecha).toLocaleString()}</TableCell>
                      <TableCell>{m.tipoDocumento || m.tipoMov || '—'}</TableCell>
                      <TableCell>{m.nroDocumento || (m.nroIDFormulario ? `#${m.nroIDFormulario}` : '—')}</TableCell>
                      <TableCell sx={{ maxWidth: 260, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {etiquetaTercero(m)}
                      </TableCell>
                      <TableCell align="right">{m.cantidad}</TableCell>
                    </TableRow>
                  ))}
                  {!detalleLoading && detalleMovs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No hay movimientos registrados para este lote en el depósito.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LotesSaldosInforme;
